import { useEffect, useId, useRef, useState } from 'react';
import { useFullscreen } from '../../lib/fullscreen.js';
import { useSession } from '../../lib/session.js';
import { useTheme } from '../../lib/theme.js';

/**
 * R8 — the topbar avatar menu. Decision #7: Fullscreen, Keyboard shortcuts, and
 * Sign out move OFF the always-visible topbar and INTO this menu, so the bar
 * stays a clean status surface. Each item is wired to the same handler it had
 * before (no new behaviour):
 *   - Fullscreen → the existing `useFullscreen` hook (omitted when unsupported).
 *   - Keyboard shortcuts → `onOpenShortcuts` (App still owns the ShortcutHelp
 *     dialog + the `?` keyboard binding).
 *   - Sign out → `useSession().signOut` (shown only when a cookie session is
 *     actually signed in, mirroring the prior header logic).
 *
 * The menu closes on Escape, on an outside click, and after any item activates.
 */
export function AvatarMenu({ onOpenShortcuts }: { onOpenShortcuts: () => void }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const { isFullscreen, toggle: toggleFullscreen, supported: fsSupported } = useFullscreen();
  const { state, signOut } = useSession();
  const { theme, toggle: toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const canSignOut = state.status === 'authenticated';

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // a11y floor — return focus to the trigger (full roving/trap is R24).
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={rootRef} className="app-avatar-wrap">
      <button
        ref={buttonRef}
        type="button"
        className="app-avatar"
        data-testid="avatar-menu-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label="Account and app menu"
        title="Theme · Fullscreen · Keyboard shortcuts · Sign out"
        onClick={() => setOpen((v) => !v)}
      >
        <UserGlyph />
      </button>
      {open ? (
        <div id={menuId} role="menu" className="app-avatar-menu" data-testid="avatar-menu">
          <button
            type="button"
            role="menuitem"
            className="app-menu-item"
            data-testid="menu-theme"
            onClick={() => run(toggleTheme)}
          >
            <span>{isDark ? 'Light theme' : 'Dark theme'}</span>
            <span className="app-menu-key" aria-hidden>
              {isDark ? '☀' : '☾'}
            </span>
          </button>
          {fsSupported ? (
            <button
              type="button"
              role="menuitem"
              className="app-menu-item"
              data-testid="menu-fullscreen"
              onClick={() => run(() => void toggleFullscreen())}
            >
              <span>{isFullscreen ? 'Exit full-screen' : 'Full-screen'}</span>
              <span className="app-menu-key" aria-hidden>
                ⤢
              </span>
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="app-menu-item"
            data-testid="menu-shortcuts"
            onClick={() => run(onOpenShortcuts)}
          >
            <span>Keyboard shortcuts</span>
            <span className="app-menu-key" aria-hidden>
              ?
            </span>
          </button>
          {canSignOut ? (
            <button
              type="button"
              role="menuitem"
              className="app-menu-item app-menu-item-danger"
              data-testid="sign-out"
              onClick={() => run(() => void signOut())}
            >
              Sign out
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function UserGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}
